import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import JsonView from '@uiw/react-json-view';
import { useMemo, useState } from 'react';
import {
  useJourneyMapResolvedProviderByName
} from '../../../hooks/journey-map-hooks';
import { useAuthenticateUser } from '../../../hooks/useAuthenticateUser';
import {
  JourneyMapResolvedProviderOption,
  isBuiltInJourneyMapProvider,
  normalizeProviderKey,
} from '../../../support/journeyMap/providers';
import { fetchItems, getApiHeaders } from '../../../support/queries';
import {
  JourneyMapAutoCompleteType,
  JourneyMapCompletionConfigValue,
  JourneyMapCompletionMode,
  JourneyMapCompletionProviderRule
} from '../../../types/journeyMap';

export const DEFAULT_JOURNEY_MAP_COMPLETION_CONFIG: JourneyMapCompletionConfigValue = {
  mode: 'manual',
  autoCompleteType: null,
  thresholdPercent: null,
};

type Props = {
  value?: Partial<JourneyMapCompletionConfigValue> | null;
  item?: any;
  onChange: (value: JourneyMapCompletionConfigValue) => void;
};

async function resolveReferenceValue(
  item: any,
  provider: JourneyMapResolvedProviderOption,
): Promise<any> {
  const referenceField = provider.reference_field || 'content_ref_id';

  // 1. Try direct lookup on the item first
  const directValue = getReferenceValue(item, referenceField);
  if (directValue !== undefined && directValue !== null && directValue !== '') {
    return directValue;
  }

  // 2. Try resolving through the referenced content record
  if (item?.content_ref_type && item?.content_ref_id) {
    const resourceName = String(item.content_ref_type).replace(/_/g, '-');
    const path = `db/${resourceName}/${item.content_ref_id}/`;

    const referencedRecord = await fetchItems(path, {}, { includePageSize: false });
    const resolvedValue = getReferenceValue(referencedRecord, referenceField);

    if (resolvedValue !== undefined && resolvedValue !== null && resolvedValue !== '') {
      return resolvedValue;
    }

    throw new Error(
      `Referenced record "${path}" does not contain reference field "${referenceField}".`,
    );
  }

  return '';
}

function normalizeConfig(
  value?: Partial<JourneyMapCompletionConfigValue> | null,
): JourneyMapCompletionConfigValue {
  return {
    mode: value?.mode || DEFAULT_JOURNEY_MAP_COMPLETION_CONFIG.mode,
    autoCompleteType:
      value?.autoCompleteType === undefined
        ? DEFAULT_JOURNEY_MAP_COMPLETION_CONFIG.autoCompleteType
        : value.autoCompleteType,
    thresholdPercent:
      value?.thresholdPercent === undefined
        ? DEFAULT_JOURNEY_MAP_COMPLETION_CONFIG.thresholdPercent
        : value.thresholdPercent,
  };
}

function getDefaultThreshold(autoCompleteType: JourneyMapAutoCompleteType | null): number | null {
  if (autoCompleteType === 'course_completion') return 90;
  if (autoCompleteType === 'assessment_score') return 80;
  return null;
}

function getRuleForType(
  provider: JourneyMapResolvedProviderOption | null,
  autoCompleteType: JourneyMapAutoCompleteType | null,
): JourneyMapCompletionProviderRule | null {
  if (!provider || !autoCompleteType) return null;
  if (isBuiltInJourneyMapProvider(provider)) return null;

  return (
    provider.rules?.find(
      (rule) => rule.auto_complete_type === autoCompleteType && rule.is_active !== false,
    ) || null
  );
}

function getValueAtPath(obj: any, path: string): any {
  if (!obj || !path) return undefined;
  return path.split('.').reduce((acc, key) => {
    if (acc == null) return undefined;
    return acc[key];
  }, obj);
}

function getReferenceValue(item: any, referenceField?: string): any {
  if (!item || !referenceField) return '';
  if (referenceField.includes('.')) {
    return getValueAtPath(item, referenceField);
  }
  return item?.[referenceField];
}

function evaluateCompletionResult(
  rule: JourneyMapCompletionProviderRule,
  thresholdPercent: number | null,
  responseBody: any,
): {
  passed: boolean;
  actualValue: any;
  reason: string;
} {
  const actualValue = rule.response_attribute
    ? getValueAtPath(responseBody, rule.response_attribute)
    : responseBody;

  if (rule.response_process === 'boolean_true') {
    const passed = actualValue === true;
    return {
      passed,
      actualValue,
      reason: passed
        ? 'Response attribute evaluated to true.'
        : 'Response attribute did not evaluate to true.',
    };
  }

  if (rule.response_process === 'status_match') {
    const normalized = String(actualValue ?? '')
      .trim()
      .toLowerCase();

    const passStatuses = (rule.pass_statuses || []).map((status) =>
      String(status).trim().toLowerCase(),
    );

    const passed = passStatuses.includes(normalized);

    return {
      passed,
      actualValue,
      reason: passed
        ? `Status matched one of: ${passStatuses.join(', ')}`
        : `Status did not match one of: ${passStatuses.join(', ')}`,
    };
  }

  const numericValue = Number(actualValue);
  const threshold = Number(thresholdPercent ?? 0);
  const passed = !Number.isNaN(numericValue) && numericValue >= threshold;

  return {
    passed,
    actualValue,
    reason: passed
      ? `Numeric value ${numericValue} met threshold ${threshold}.`
      : `Numeric value ${String(actualValue)} did not meet threshold ${threshold}.`,
  };
}

function getRuleSummary(rule: JourneyMapCompletionProviderRule | null) {
  if (!rule) return 'No provider rule resolved.';
  if (rule.response_process === 'numeric_gte') {
    return `Uses numeric evaluation from "${rule.response_attribute || 'response'}".`;
  }
  if (rule.response_process === 'boolean_true') {
    return `Uses boolean evaluation from "${rule.response_attribute || 'response'}".`;
  }
  return `Uses status match from "${rule.response_attribute || 'response'}".`;
}

export default function JourneyMapCompletionConfig({
                                                     value,
                                                     item,
                                                     onChange,
                                                   }: Props) {
  const { email } = useAuthenticateUser();
  const config = useMemo(() => normalizeConfig(value), [value]);
  const provider = useJourneyMapResolvedProviderByName(item?.provider || null);

  const [exampleEmail, setExampleEmail] = useState(email);
  const [testOpen, setTestOpen] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [testError, setTestError] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{
    request?: any;
    response?: any;
    evaluation?: {
      passed: boolean;
      actualValue: any;
      reason: string;
    };
  } | null>(null);
  const [tab, setTab] = useState(0);

  const selectedRule = useMemo(() => {
    return getRuleForType(provider, config.autoCompleteType);
  }, [provider, config.autoCompleteType]);

  const supportsAutoComplete = provider?.supports_auto_complete ?? false;
  const supportsCourseCompletion = provider?.supports_course_completion ?? false;
  const supportsAssessmentScore = provider?.supports_assessment_score ?? false;

  // The video provider auto-completes by watched coverage, which is enforced server-side from the
  // item's video completion threshold rather than a course/assessment rule. So it supports
  // Auto-Complete, but there is no rule type to pick or endpoint to test here — show an info note
  // instead of the (empty) rule config.
  const isWatchedCoverageProvider =
    !!provider && normalizeProviderKey(provider.provider_key) === 'video';

  const availableAutoCompleteOptions = useMemo(() => {
    const options: { value: JourneyMapAutoCompleteType; label: string }[] = [];

    if (supportsCourseCompletion) {
      options.push({
        value: 'course_completion',
        label: 'Course Completion',
      });
    }

    if (supportsAssessmentScore) {
      options.push({
        value: 'assessment_score',
        label: 'Assessment Score',
      });
    }

    return options;
  }, [supportsCourseCompletion, supportsAssessmentScore]);

  const update = (changes: Partial<JourneyMapCompletionConfigValue>) => {
    onChange({
      ...config,
      ...changes,
    });
  };

  const handleModeChange = (mode: JourneyMapCompletionMode) => {
    if (mode === 'manual') {
      update({
        mode,
        autoCompleteType: null,
        thresholdPercent: null,
      });
      return;
    }

    if (mode === 'manager') {
      update({
        mode,
        autoCompleteType: null,
        thresholdPercent: null,
      });
      return;
    }

    const nextType =
      config.autoCompleteType ||
      availableAutoCompleteOptions[0]?.value ||
      null;

    update({
      mode,
      autoCompleteType: nextType,
      thresholdPercent:
        config.thresholdPercent == null
          ? getDefaultThreshold(nextType)
          : config.thresholdPercent,
    });
  };

  const handleAutoCompleteTypeChange = (autoCompleteType: JourneyMapAutoCompleteType) => {
    update({
      autoCompleteType,
      thresholdPercent: getDefaultThreshold(autoCompleteType),
    });
  };

  const handleRunTest = async () => {
    try {
      setTab(0);
      setTestOpen(true);
      setTestLoading(true);
      setTestError(null);
      setTestResult(null);

      if (config.mode !== 'auto_complete') {
        throw new Error('Completion mode must be Auto-Complete to run a completion test.');
      }

      if (!provider) {
        throw new Error(`No completion provider registry found for provider "${item?.provider || ''}".`);
      }

      if (isBuiltInJourneyMapProvider(provider)) {
        throw new Error(
          `Provider "${provider.display_name}" does not support auto-complete test execution.`,
        );
      }

      if (!selectedRule) {
        throw new Error('No active completion rule found for the selected auto-complete type.');
      }

      const referenceField = provider.reference_field || 'content_ref_id';
      const referenceValue = await resolveReferenceValue(item, provider);

      if (referenceValue === undefined || referenceValue === null || referenceValue === '') {
        throw new Error(
          `No reference value found on the selected item or referenced content for reference field "${referenceField}".`,
        );
      }

      if (!selectedRule.endpoint) {
        throw new Error('Selected provider rule does not have an endpoint configured.');
      }

      const params: Record<string, any> = {
        [provider.email_param || 'email']: exampleEmail,
        [selectedRule.reference_param || 'course_id']: referenceValue,
      };

      const requestPreview = {
        url: selectedRule.endpoint,
        method: 'GET',
        headers: getApiHeaders(null, 'GET', selectedRule.endpoint),
        params,
      };

      const responseBody = await fetchItems(selectedRule.endpoint, params, {
        includePageSize: false,
      });

      const evaluation = evaluateCompletionResult(
        selectedRule,
        config.thresholdPercent,
        responseBody,
      );

      setTestResult({
        request: requestPreview,
        response: responseBody,
        evaluation,
      });
    } catch (error: any) {
      setTestError(error?.message || 'Failed to test completion status.');
    } finally {
      setTestLoading(false);
    }
  };

  const modeOptions: { value: JourneyMapCompletionMode; label: string; disabled?: boolean }[] = [
    { value: 'manual', label: 'Manual' },
    { value: 'manager', label: 'Manager Approval' },
    {
      value: 'auto_complete',
      label: 'Auto-Complete',
      disabled: !supportsAutoComplete,
    },
  ];

  const showAutoComplete = config.mode === 'auto_complete';

  return (
    <Stack spacing={2}>
      <Divider />

      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        Completion Tracking
      </Typography>

      <FormControl size="small" fullWidth>
        <InputLabel id="completion-mode-label">Completion Mode</InputLabel>
        <Select
          labelId="completion-mode-label"
          label="Completion Mode"
          value={config.mode}
          onChange={(e) => handleModeChange(e.target.value as JourneyMapCompletionMode)}
        >
          {modeOptions.map((option) => (
            <MenuItem
              key={option.value}
              value={option.value}
              disabled={!!option.disabled}
            >
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {config.mode === 'auto_complete' && !provider && (
        <Alert severity="warning">
          This item uses Auto-Complete, but no provider registry entry was found for provider{' '}
          <strong>{item?.provider || 'Unknown'}</strong>.
        </Alert>
      )}

      {showAutoComplete && isWatchedCoverageProvider && (
        <Alert severity="info">
          Completion is credited automatically by <strong>watched coverage</strong>. The learner
          completes this step once they watch at least the video&apos;s completion threshold, which
          you set in the Video settings for this step — no rule configuration is needed here.
        </Alert>
      )}

      {showAutoComplete && !!provider && !isWatchedCoverageProvider && (
        <>
          <FormControl size="small" fullWidth>
            <InputLabel id="auto-complete-type-label">Auto-Complete Type</InputLabel>
            <Select
              labelId="auto-complete-type-label"
              label="Auto-Complete Type"
              value={config.autoCompleteType || ''}
              onChange={(e) =>
                handleAutoCompleteTypeChange(e.target.value as JourneyMapAutoCompleteType)
              }
            >
              {availableAutoCompleteOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label={
              config.autoCompleteType === 'course_completion'
                ? 'Minimum Course Completion Percent'
                : config.autoCompleteType === 'assessment_score'
                  ? 'Minimum Assessment Score Percent'
                  : 'Threshold Percent'
            }
            size="small"
            type="number"
            value={config.thresholdPercent ?? ''}
            onChange={(e) =>
              update({
                thresholdPercent: e.target.value === '' ? null : Number(e.target.value),
              })
            }
            fullWidth
          />

          <Box
            sx={{
              p: 1.5,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1.5,
              backgroundColor: 'background.default',
            }}
          >
            <Stack spacing={0.75}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                Provider Defaults
              </Typography>

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip size="small" label={`Provider: ${provider.display_name}`} />
                {selectedRule && (
                  <Chip
                    size="small"
                    label={`Rule: ${selectedRule.auto_complete_type.replace('_', ' ')}`}
                  />
                )}
              </Stack>

              <Typography variant="caption" color="text.secondary">
                {getRuleSummary(selectedRule)}
              </Typography>
            </Stack>
          </Box>

          <Divider />

          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            Test Completion Status
          </Typography>

          <TextField
            label="Example Email"
            size="small"
            value={exampleEmail}
            onChange={(e) => setExampleEmail(e.target.value)}
            fullWidth
          />

          <Button
            variant="outlined"
            onClick={handleRunTest}
            disabled={!provider || !selectedRule}
          >
            Test Completion Status
          </Button>
        </>
      )}

      <Dialog open={testOpen} onClose={() => setTestOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Completion Status Test</DialogTitle>
        <DialogContent dividers>
          {testLoading ? (
            <Typography variant="body2">Running completion test...</Typography>
          ) : testError ? (
            <Alert severity="error">{testError}</Alert>
          ) : (
            <>
              {testResult?.evaluation && (
                <Alert severity={testResult.evaluation.passed ? 'success' : 'warning'} sx={{ mb: 2 }}>
                  {testResult.evaluation.passed ? 'PASS' : 'FAIL'} — {testResult.evaluation.reason}
                </Alert>
              )}

              <Tabs value={tab} onChange={(_, nextTab) => setTab(nextTab)}>
                <Tab label="Request" />
                <Tab label="Response" />
                <Tab label="Evaluation" />
              </Tabs>

              <Box sx={{ mt: 2 }}>
                {tab === 0 && (
                  <JsonView
                    value={testResult?.request || {}}
                    keyName={null as any}
                    displayDataTypes={false}
                    displayObjectSize={false}
                    enableClipboard
                  />
                )}

                {tab === 1 && (
                  <JsonView
                    value={testResult?.response || {}}
                    keyName={null as any}
                    displayDataTypes={false}
                    displayObjectSize={false}
                    enableClipboard
                  />
                )}

                {tab === 2 && (
                  <JsonView
                    value={testResult?.evaluation || {}}
                    keyName={null as any}
                    displayDataTypes={false}
                    displayObjectSize={false}
                    enableClipboard
                  />
                )}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestOpen(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}